import OfferApi from "api/offer/offerApi";
import documentApi from "../../../features/document/edit/documents/api.js";
import * as types from "../../actionTypes";
import * as eventTypes from "../../eventTypes";
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from "../httpActions";
import { showError } from "actions/actionCreators/snackbarActions";
import { showLoading } from "actions/actionCreators/httpActions";

function loadOffersSuccess() {
  return { type: types.LOAD_OFFERS_SUCCESS };
}

function loadOfferHeaderStarted(/*offer*/) {
  return { type: types.LOAD_OFFER_HEADER_STARTED };
}

function loadOfferHeaderSuccess(offer) {
  return { type: types.LOAD_OFFER_HEADER_SUCCESS, offer };
}

function loadOfferFinancialProductRuleReadOnlyFieldsSuccess(offerFinancialProductRuleReadOnlyFields) {
  return {
    type: types.LOAD_OFFER_FINANCIAL_PRODUCT_RULE_READONLY_FIELDS_SUCCESS,
    offerFinancialProductRuleReadOnlyFields
  };
}

function resetCurrentOfferAction(documentId, siteId) {
  return { type: types.RESET_CURRENT_OFFER, payload: { documentId, siteId } };
}

export function loadOffers(filters, pageRequest) {
  return async dispatch => {
    try {
      const offers = await dispatch(sendHttpRequest(OfferApi.getAllOffers(filters, pageRequest)));
      dispatch(loadOffersSuccess());

      return offers;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function resetCurrentOffer(documentId, siteId) {
  return dispatch => {
    try {
      dispatch(resetCurrentOfferAction(documentId, siteId));
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadOfferFinancialProductRuleReadOnlyFields(documentId, siteId) {
  return async (dispatch, getState) => {
    let offerFinancialProductRuleReadOnlyFields = getState().currentOffer.offerFinancialProductRuleReadOnlyFields;
    if (offerFinancialProductRuleReadOnlyFields.isLoaded || offerFinancialProductRuleReadOnlyFields.loading)
      return offerFinancialProductRuleReadOnlyFields;

    try {
      dispatch({ type: types.LOAD_OFFER_FINANCIAL_PRODUCT_RULE_READONLY_FIELDS_STARTED });
      offerFinancialProductRuleReadOnlyFields = await dispatch(
        sendHttpRequest(OfferApi.getOfferFinancialProductRuleReadOnlyFields(documentId, siteId))
      );
      dispatch(loadOfferFinancialProductRuleReadOnlyFieldsSuccess(offerFinancialProductRuleReadOnlyFields));
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadOfferHeader(documentId, siteId) {
  return async (dispatch, getState) => {
    const { loading } = getState().currentOffer.header;
    if (loading) {
      return;
    }

    try {
      dispatch(loadOfferHeaderStarted());
      const offerHeader = await dispatch(sendHttpRequest(OfferApi.getOfferHeader(documentId, siteId)));
      dispatch(loadOfferHeaderSuccess(offerHeader));
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadOfferReports(documentId, siteId) {
  return async (dispatch, getState) => {
    let offerReports = getState().currentOffer.offerReports;
    if (offerReports && offerReports.isLoaded) {
      return;
    }

    try {
      await dispatch(
        sendHttpCommandAndWaitForEvent(
          OfferApi.getOfferReports(documentId, siteId),
          "GetOfferReportsCommand",
          [eventTypes.OFFER_REPORTS_EXECUTED],
          false
        )
      );
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function generateDocumentReport(report) {
  return async dispatch => {
    dispatch({ type: types.GENERATE_DOCUMENT_REPORT_STARTED, key: report.reportId });

    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(OfferApi.generateDocumentReport(report), null, [
          eventTypes.DOCUMENT_REPORT_GENERATED
        ])
      );
      if (event.ok) {
        const url = documentApi.buildExportDownloadUrl(event.eventBody.filePath, event.eventBody.fileName);
        dispatch({ type: types.GENERATE_DOCUMENT_REPORT_SUCCEEDED, key: report.reportId, url });
        window.location = url;
      } else {
        dispatch({ type: types.GENERATE_DOCUMENT_REPORT_FAILED, key: report.reportId });
      }
    } catch (error) {
      dispatch({ type: types.GENERATE_DOCUMENT_REPORT_FAILED, key: report.reportId });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function createOffer(offer) {
  return async dispatch => {
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(OfferApi.createOffer(offer), "RunningOfferCommand", [eventTypes.OFFER_CREATED])
    );
    return event;
  };
}

export function loadOfferConfiguration(documentTypeId) {
  return async (dispatch, getState) => {
    let offerConfig = getState().offerConfig;
    if (offerConfig.isLoaded) return;

    try {
      offerConfig = await dispatch(sendHttpRequest(OfferApi.getOfferConfig(documentTypeId)));
      dispatch({ type: types.LOAD_OFFER_CONFIG_SUCCESS, payload: { offerConfig } });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadUserPreferences() {
  return async (dispatch, getState) => {
    try {
      let allUserPreferences = getState().offerVisible.userPreferences;
      if (allUserPreferences.isLoaded) return;

      allUserPreferences = await dispatch(sendHttpRequest(OfferApi.loadUserPreferences()));
      dispatch({ type: types.LOAD_OFFER_USER_PREFERENCES_SUCCESS, payload: allUserPreferences });

      const implicitPreference = allUserPreferences.find(x => x.implicit === true);
      dispatch({ type: types.LOAD_OFFER_SELECTED_USER_PREFERENCE_SUCCESS, payload: implicitPreference });

      const defaultPreference = allUserPreferences.find(x => x.isDefault === true);
      dispatch({ type: types.LOAD_OFFER_DEFAULT_USER_PREFERENCE_SUCCESS, payload: defaultPreference });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function updateUserPreferences(userPreferences) {
  return async dispatch => {
    try {
      await dispatch(sendHttpRequest(OfferApi.updateUserPreferences(userPreferences)));
      dispatch({ type: types.UPDATE_OFFER_USER_PREFERENCES_SUCCESS, payload: userPreferences });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function updateSelectedUserPreference(selectedUserPreference) {
  return async dispatch => {
    try {
      dispatch({ type: types.UPDATE_OFFER_SELECTED_USER_PREFERENCE_SUCCESS, payload: selectedUserPreference });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function submitPreApproval(documentId, siteId) {
  return async dispatch => {
    try {
      await dispatch(
        sendHttpCommandAndWaitForEvent(
          OfferApi.submitOfferPreApproval(documentId, siteId),
          "OfferPreApprovalInProgress",
          [eventTypes.OFFER_PREAPPROVAL_SUBMITTED]
        )
      );
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function cloneOffer(documentId, siteId, offerNumber) {
  return async dispatch => {
    try {
      dispatch(showLoading(true));
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          OfferApi.cloneOffer({ documentId, siteId, offerNumber }),
          "OfferCloningInProgress",
          [eventTypes.OFFER_CLONE_CREATED],
          false
        )
      );
      dispatch(showLoading(false));

      return event;
    } catch (error) {
      dispatch(showLoading(false));
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function copyOffer(documentId, siteId) {
  return async dispatch => {
    try {
      dispatch(showLoading(true));
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          OfferApi.copyOffer({ documentId, siteId }),
          "OfferCloningInProgress",
          [eventTypes.OFFER_COPIED],
          false
        )
      );
      dispatch(showLoading(false));

      return event;
    } catch (error) {
      dispatch(showLoading(false));
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadOfferWarnings(documentId, siteId) {
  if (!(documentId || siteId)) return;
  return async dispatch => {
    // I don't think this can be cached.
    // The warnings can change at any time from different places.
    try {
      let documentWarnings = await dispatch(sendHttpRequest(OfferApi.getOfferWarnings(documentId, siteId)));
      dispatch({ type: types.LOAD_OFFER_WARNINGS_SUCCESS, payload: { documentWarnings } });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function exportOffers(filters, fieldsToShow) {
  return dispatch => {
    return OfferApi.exportOffers(filters, fieldsToShow).catch(err => {
      dispatch(showError(err.message));
      throw err;
    });
  };
}

export function buildOfferExportDownloadUrl(filePath, fileName) {
  return () => OfferApi.buildOfferExportDownloadUrl(filePath, fileName);
}

export function selectFolder(documentId, siteId, folderId, folderSiteId) {
  return async dispatch => {
    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          OfferApi.selectFolder(documentId, siteId, folderId, folderSiteId),
          "FolderSelectionInProgress",
          [eventTypes.OFFER_FOLDER_UPDATED]
        )
      );
      
      return event;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadNavigationStepWarnings(documentId, siteId, navigationStep) {
  if (!(documentId || siteId)) return;
  return async dispatch => {
    try {
      let navStepWarning = await dispatch(
        sendHttpRequest(OfferApi.getNavigationStepWarnings(documentId, siteId, navigationStep))
      );
      return navStepWarning;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export * from "../../../features/offer/edit/assets/asset/actionCreators";
export * from "./offerStatesActions";
